"use client";

import { c as _c } from "react/compiler-runtime";
import { UnifiedErrorPanel } from "@/components/errors/UnifiedErrorPanel";
import { EnhancedJsonViewWithCopy } from "@/components/ui/enhanced-json-view";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAutoViewPreference } from "@/hooks/useAutoViewPreference";
import type { FixWithAIErrorContext } from "@/hooks/useFixWithAI";
import type { SlackChannel } from "@/app/(v2)/workflows/_components/form-fields/slackChannels";
import { AutoResultView } from "@/components/workflow/auto-view";

/**
 * ErrorDisplay - Renders error messages using the UnifiedErrorPanel.
 * Provides AI analysis button and consistent error display across the app.
 */
export function ErrorDisplay(t0) {
  const $ = _c(9);
  const {
    error,
    nodeInfo,
    nodeState,
    nodeConfig,
    workflowId,
    runId,
    onFixWithAI,
    onGetSupport
  } = t0;
  const t1 = nodeState as any;
  let t2;
  if ($[0] !== error || $[1] !== nodeConfig || $[2] !== nodeInfo || $[3] !== onFixWithAI || $[4] !== onGetSupport || $[5] !== runId || $[6] !== t1 || $[7] !== workflowId) {
    t2 = <UnifiedErrorPanel error={error} variant="inline" isTestRun={false} showVersionCompare={true} showFieldEditor={false} nodeInfo={nodeInfo} nodeState={t1} nodeConfig={nodeConfig} workflowId={workflowId} runId={runId} onFixWithAI={onFixWithAI} onGetSupport={onGetSupport} />;
    $[0] = error;
    $[1] = nodeConfig;
    $[2] = nodeInfo;
    $[3] = onFixWithAI;
    $[4] = onGetSupport;
    $[5] = runId;
    $[6] = t1;
    $[7] = workflowId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/**
 * ResultDisplay - Renders execution results with error handling.
 * Offers an Auto (rich) / JSON (raw) toggle; the choice is a shared,
 * localStorage-backed preference (see useAutoViewPreference).
 */
export function ResultDisplay(t0) {
  const $ = _c(24);
  const {
    result,
    expandAll: t1,
    nodeInfo,
    nodeState,
    workflowId,
    runId,
    onFixWithAI,
    onGetSupport,
    slackChannelsMap
  } = t0;
  const expandAll = t1 === undefined ? false : t1;
  const {
    mode,
    setMode
  } = useAutoViewPreference();
  if (result.error && typeof result.error === "string") {
    const t2 = nodeState?.input;
    let t3;
    if ($[0] !== nodeInfo || $[1] !== nodeState || $[2] !== onFixWithAI || $[3] !== onGetSupport || $[4] !== result.error || $[5] !== runId || $[6] !== t2 || $[7] !== workflowId) {
      t3 = <ErrorDisplay error={result.error} nodeInfo={nodeInfo} nodeState={nodeState} nodeConfig={t2} workflowId={workflowId} runId={runId} onFixWithAI={onFixWithAI} onGetSupport={onGetSupport} />;
      $[0] = nodeInfo;
      $[1] = nodeState;
      $[2] = onFixWithAI;
      $[3] = onGetSupport;
      $[4] = result.error;
      $[5] = runId;
      $[6] = t2;
      $[7] = workflowId;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  let t2;
  if ($[9] !== setMode) {
    t2 = value => setMode(value === "json" ? "json" : "auto");
    $[9] = setMode;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <TabsList><TabsTrigger value="auto">Auto</TabsTrigger><TabsTrigger value="json">JSON</TabsTrigger></TabsList>;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== mode || $[13] !== t2) {
    t4 = <div className="flex justify-end"><Tabs variant="ghost" size="sm" value={mode} onValueChange={t2}>{t3}</Tabs></div>;
    $[12] = mode;
    $[13] = t2;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== expandAll || $[16] !== mode || $[17] !== nodeInfo?.type || $[18] !== result || $[19] !== slackChannelsMap) {
    t5 = <div className="emboss-inner !bg-ds-neutral-100 dark:!bg-ds-neutral-800 rounded-lg p-4 font-mono text-xs">{mode === "auto" ? <div className="font-sans"><AutoResultView value={result} nodeType={nodeInfo?.type} /></div> : <EnhancedJsonViewWithCopy value={result} defaultExpanded={true} expandAll={expandAll} showExpandAll={false} slackChannelsMap={slackChannelsMap} />}</div>;
    $[15] = expandAll;
    $[16] = mode;
    $[17] = nodeInfo?.type;
    $[18] = result;
    $[19] = slackChannelsMap;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t4 || $[22] !== t5) {
    t6 = <div className="space-y-1.5">{t4}{t5}</div>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}